import React, { useState, useMemo } from 'react';
import ReactGA from 'react-ga4';
import { useAlbums } from '../context/AlbumsContext';
import Navbar from '../components/Navbar';
import PaisDropdown from '../components/PaisDropdown';
import ProgressBar from '../components/ProgressBar';
import CountryAutocomplete from '../components/CountryAutocomplete';
import { GRUPOS_ORDENADOS, GRUPOS_ESPECIAIS, ORDEM_TIMES_POR_GRUPO } from '../data/copa2026';
import Logo from '../images/caca_figurinhas_logo_v2.svg';
import './MinhasFigurinhas.css';

const TOTAL_FIGURINHAS = 998;

const MinhasFigurinhas = ({ onNavigate }) => {
  const { albums, activeAlbumId, isLoading, stickers, toggleFigurinha, setAlbumActive } = useAlbums();
  const [filtro, setFiltro] = useState('');

  const totalObtidas = stickers
    ? Object.values(stickers).reduce((acc, pais) =>
        acc + Object.values(pais.jogadores).filter((j) => j.obtida).length, 0)
    : 0;

  const gruposEspeciais = useMemo(() => {
    if (!stickers) return [];
    return GRUPOS_ESPECIAIS.map((g) => stickers[g]).filter(Boolean);
  }, [stickers]);

  const autocompleteOptions = useMemo(() => {
    if (!stickers) return [];
    const options = [];

    // Adicionar grupos especiais
    gruposEspeciais.forEach((p) => {
      options.push({
        value: p.sigla,
        label: `${p.flag} ${p.nome}`,
        searchText: `${p.nome} ${Object.values(p.jogadores || {}).map((j) => j.nome).join(' ')}`,
        type: 'group',
      });
    });

    // Adicionar todos os países
    GRUPOS_ORDENADOS.forEach((g) => {
      Object.values(stickers)
        .filter((p) => p.grupo === g)
        .forEach((p) => {
          options.push({
            value: p.sigla,
            label: `${p.flag} ${p.nome}`,
            searchText: `${p.nome} ${Object.values(p.jogadores || {}).map((j) => j.nome).join(' ')}`,
            type: 'group',
            jogadores: p.jogadores,
            countryFlag: p.flag,
            countryName: p.nome,
          });

          // Adicionar cada jogador individualmente
          Object.entries(p.jogadores || {}).forEach(([key, jogador]) => {
            options.push({
              value: p.sigla,
              label: `${jogador.nome}`,
              searchText: jogador.nome,
              type: 'player',
              playerKey: key,
              countryFlag: p.flag,
              countryName: p.nome,
            });
          });
        });
    });

    return options;
  }, [stickers, gruposEspeciais]);

  const itemFiltrado = useMemo(() => {
    if (!filtro || !stickers) return null;
    return stickers[filtro] || null;
  }, [filtro, stickers]);

  const handleTrocarAlbum = (e) => {
    const albumId = e.target.value;
    const album = albums.find((a) => a.id === albumId);

    ReactGA.event({
      category: 'Navigation',
      action: 'Album Changed',
      label: album?.name || 'Unknown',
    });

    setAlbumActive(albumId);
  };

  const renderDropdown = (pais, expanded = false) => (
    <PaisDropdown
      key={pais.sigla}
      pais={pais}
      onToggleJogador={(sigla, key) => toggleFigurinha(activeAlbumId, sigla, key)}
      defaultExpanded={expanded}
    />
  );

  return (
    <div className="minhas-figurinhas">
      <header className="minhas-figurinhas-header">
        <div className="header-top">
          <img src={Logo} alt="Caça Figurinhas Logo" className="logo-icon" />
          <h1 className="app-title">Caça Figurinhas</h1>
        </div>

        <div className="header-controls">
          <label className="album-select-label" htmlFor="album-select">Álbum</label>
          <select
            id="album-select"
            className="album-select"
            value={activeAlbumId || ''}
            onChange={handleTrocarAlbum}
          >
            <option value="" disabled>Selecione um álbum</option>
            {albums.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>

        <div className="progresso-global">
          <ProgressBar obtidas={totalObtidas} total={TOTAL_FIGURINHAS} size="large" />
        </div>
      </header>

      <Navbar activeTab="figurinhas" onNavigate={onNavigate} activeAlbumId={activeAlbumId} isLoading={isLoading} />

      <main className="minhas-figurinhas-main">
        {!activeAlbumId ? (
          <div className="sem-album">
            <p>Nenhum álbum selecionado.</p>
            <button className="btn-voltar" onClick={() => onNavigate('albums')}>
              Ir para Meus Álbuns
            </button>
          </div>
        ) : (
          <>
            <div className="filtro-wrap">
              <CountryAutocomplete
                options={autocompleteOptions}
                value={filtro}
                onChange={setFiltro}
                placeholder="Pesquisar país ou jogador..."
                stickers={stickers}
              />
            </div>

            <div className="grupos-lista">
              {itemFiltrado ? (
                renderDropdown(itemFiltrado, true)
              ) : (
                <>
                  {/* Grupos especiais no topo */}
                  <section className="grupo-section">
                    <h2 className="grupo-titulo grupo-titulo-especial">Especiais</h2>
                    {gruposEspeciais.map((p) => renderDropdown(p))}
                  </section>

                  {/* Grupos A–L */}
                  {GRUPOS_ORDENADOS.map((grupo) => {
                    const paisesDoGrupo = stickers
                      ? (ORDEM_TIMES_POR_GRUPO[grupo] || []).map((sigla) => stickers[sigla]).filter(Boolean)
                      : [];
                    return (
                      <section key={grupo} className="grupo-section">
                        <h2 className="grupo-titulo">Grupo {grupo}</h2>
                        {paisesDoGrupo.map((pais) => renderDropdown(pais))}
                      </section>
                    );
                  })}
                </>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default MinhasFigurinhas;
